if (!customElements.get('dbc-video-carousel')) {
  customElements.define('dbc-video-carousel', class extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-slide-id]'));
      this.inView = false;
      this.autoplay = this.dataset.autoplay === 'true' && this.dataset.editor !== 'true';
      this.onReady = () => this.initSwiper();
      this.onMetadata = () => this.swiper?.updateAutoHeight(0);
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.onDeviceChange = () => {
        if (!this.swiper) return;
        this.syncPlayback();
        this.swiper.updateAutoHeight(0);
      };
      this.mobileQuery.addEventListener('change', this.onDeviceChange);
      this.onKeydown = (event) => {
        if (!this.swiper || !event.target.matches('.dbc-video-carousel__stage, [data-direction]')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        if (event.key === 'ArrowRight') this.swiper.slideNext();
        else this.swiper.slidePrev();
      };
      this.onEnded = (event) => {
        if (this.swiper && this.dataset.advance === 'true' && this.slides.length > 1 && event.target === this.currentVideo()) {
          this.swiper.slideNext();
        }
      };
      this.onBlockSelect = (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.slideId === event.detail.blockId);
        if (index !== -1) this.swiper?.slideTo(index, 0);
      };
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('loadedmetadata', this.onMetadata, true);
      this.addEventListener('load', this.onMetadata, true);
      this.addEventListener('ended', this.onEnded, true);
      this.addEventListener('shopify:block:select', this.onBlockSelect);
      this.observer = new IntersectionObserver(([entry]) => {
        this.inView = entry.isIntersecting;
        if (this.inView) this.playCurrent();
        else this.pauseAll();
      });
      this.observer.observe(this.querySelector('.dbc-video-carousel__stage'));
      // Swiper 11 is already included globally by layout/theme.liquid.
      this.libraryScript = document.querySelector('script[src*="swiper-bundle.min.js"]');
      this.libraryScript?.addEventListener('load', this.onReady, { once: true });
      this.initSwiper();
    }

    initSwiper() {
      if (!this.isConnected || this.swiper || !window.Swiper || !this.slides.length) return;
      this.swiper = new window.Swiper(this.querySelector('.swiper'), {
        init: false,
        slidesPerView: 1,
        autoHeight: true,
        rewind: true,
        speed: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 400,
        spaceBetween: 16,
        watchOverflow: true,
        navigation: {
          prevEl: this.querySelector('[data-direction="-1"]'),
          nextEl: this.querySelector('[data-direction="1"]')
        },
        a11y: {
          prevSlideMessage: 'Previous slide',
          nextSlideMessage: 'Next slide',
          slideLabelMessage: '{{index}} / {{slidesLength}}'
        },
        on: {
          init: () => this.syncPlayback(),
          slideChange: () => this.syncPlayback()
        }
      });
      this.swiper.init();
    }

    disconnectedCallback() {
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('loadedmetadata', this.onMetadata, true);
      this.removeEventListener('load', this.onMetadata, true);
      this.removeEventListener('ended', this.onEnded, true);
      this.removeEventListener('shopify:block:select', this.onBlockSelect);
      this.libraryScript?.removeEventListener('load', this.onReady);
      this.mobileQuery.removeEventListener('change', this.onDeviceChange);
      this.observer.disconnect();
      this.pauseAll();
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    pauseAll() {
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    currentVideo() {
      const device = this.mobileQuery.matches ? 'mobile' : 'desktop';
      return this.slides[this.swiper?.activeIndex ?? 0]?.querySelector(
        `.dbc-video-carousel__video--${device}, .dbc-video-carousel__video--shared`
      );
    }

    playCurrent() {
      const video = this.currentVideo();
      if (!video || !this.autoplay || !this.inView || !this.swiper) return;
      video.muted = true;
      video.play().catch(() => {});
    }

    syncPlayback() {
      this.pauseAll();
      this.slides.forEach((slide, index) => {
        const active = index === this.swiper.activeIndex;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.playCurrent();
    }
  });
}
